import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import {Checkpoint} from '@/model/network/Checkpoint';

import {INetworkNetworkingService} from '@/services/network/INetworkNetworkingService';

export class NetworkRestoreProcedure {
  public timeout!: Date;

  private logger: ILogger = new LogdownLogger('RestoreProcedure');

  private tickHandle!: any;

  private checkpoint!: Checkpoint;

  /**
   * A restore procedure creates a checkpoint
   * @param networkingService Service used for the networking features access
   * @param duration Duration in seconds before the old config is restored
   * @param tickInterval Interval in seconds of the internal timer
   * @param devices List of devices concerned by the restoration point. Use null to set all devices.
   * @param tickCallback Callback called at each tick
   * @param destroyExisting When creating a new checkpoint, destroy all existing ones.
   * @param allowOverlapping
   * @param deleteNewConnectionOnRollback Upon rollback, delete any new connection added after the checkpoint
   * @param disconnectNewDevicesOnRollback Upon rollback, disconnect any new device appeared after the checkpoint
   */
  constructor(private networkingService: INetworkNetworkingService,
              public duration: number,
              public tickInterval: number,
              public devices?: string[],
              private tickCallback?: any,
              private destroyExisting?: boolean,
              private allowOverlapping?: boolean,
              private deleteNewConnectionOnRollback?: boolean,
              private disconnectNewDevicesOnRollback?: boolean,
  ) {

  }

  public async start() {
    this.timeout = new Date();
    this.timeout.setSeconds(this.timeout.getSeconds() + this.duration);

    this.logger.debug(`Starting restore procedure`);

    this.tickHandle = setInterval(this.tick.bind(this), this.tickInterval * 1000);

    try {
      this.logger.debug(`Creating checkpoint...`);
      this.checkpoint = await this.networkingService.createCheckpoint({
        devices: this.devices,
        timeout: this.duration,
        destroyExisting: this.destroyExisting,
        allowOverlapping: this.allowOverlapping,
        deleteNewConnectionOnRollback: this.deleteNewConnectionOnRollback,
        disconnectNewDevicesOnRollback: this.disconnectNewDevicesOnRollback,
      });

      this.logger.debug(`Checkpoint successfully created`, this.checkpoint);

    } catch (error) {
      this.logger.error(`Could not create networking configuration checkpoint`);
      clearInterval(this.tickHandle);
      throw error;
    }
  }

  public async cancel() {
    this.logger.debug(`Removing restoration checkpoint`, this.checkpoint);
    await this.networkingService.deleteCheckpoint(this.checkpoint.Id);
    this.logger.debug(`Restoration checkpoint has bee removed`, this.checkpoint);

    clearInterval(this.tickHandle);
  }

  public async rollback() {
    this.logger.debug(`Rollback restoration checkpoint`, this.checkpoint);
    await this.networkingService.rollbackCheckpoint(this.checkpoint.Id);
    this.logger.debug(`Restoration checkpoint is rolling back`, this.checkpoint);

    clearInterval(this.tickHandle);
  }

  private tick() {
    if (this.timeout > new Date()) {
      if (this.tickCallback) {
        this.tickCallback();
      }
    } else {
      clearInterval(this.tickHandle);
      this.logger.trace(`Timeout occurred`);
    }
  }
}
